import React, { useCallback, useEffect, useMemo } from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

function ImagePreview({ data, selectedImage, setselectedImage, setopen, apiImage }: any) {
  const imageList = useMemo(() => {
    const api = (apiImage ?? []).map((img: any) => `${process.env.REACT_APP_API}${img.imageUrl}`);
    const local = (data ?? []).map((item: any) => item.url);
    return [...api, ...local];
  }, [apiImage, data]);

  const currentIndex = useMemo(
    () => imageList.findIndex((url) => url === selectedImage),
    [imageList, selectedImage]
  );

  const goPrev = useCallback(() => {
    if (imageList.length <= 1) return;
    const prev = currentIndex <= 0 ? imageList.length - 1 : currentIndex - 1;
    setselectedImage(imageList[prev]);
  }, [imageList, currentIndex, setselectedImage]);

  const goNext = useCallback(() => {
    if (imageList.length <= 1) return;
    const next = currentIndex < 0 || currentIndex >= imageList.length - 1 ? 0 : currentIndex + 1;
    setselectedImage(imageList[next]);
  }, [imageList, currentIndex, setselectedImage]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setopen(false);
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setopen, goPrev, goNext]);

  const selectImage = (url: string) => setselectedImage(url);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95 backdrop-blur-sm">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="text-sm font-medium text-white/80">Image preview</span>
        <button
          onClick={() => setopen(false)}
          className="flex h-10 w-10 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white"
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </button>
      </div>

      {/* Main Image */}
      <div className="flex flex-1 items-center justify-center overflow-hidden p-6">
        <div className="flex h-full max-h-[calc(100vh-12rem)] w-full max-w-5xl items-center justify-center rounded-xl border border-white/10 bg-black/40 p-4 shadow-2xl">
          <img
            src={selectedImage}
            alt="Preview"
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>

      {/* Thumbnails */}
      <div className="shrink-0 border-t border-white/10 bg-black/60 px-4 py-4">
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={goPrev}
            disabled={imageList.length <= 1}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous image"
          >
            <ArrowBackIosIcon sx={{ fontSize: 18 }} />
          </button>

          <div className="flex max-w-2xl flex-1 gap-3 overflow-x-auto py-2 md:max-w-3xl [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20">
            {apiImage?.map((image: any, i: number) => (
              <button
                key={i}
                type="button"
                onClick={() => selectImage(`${process.env.REACT_APP_API}${image.imageUrl}`)}
                className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  selectedImage === `${process.env.REACT_APP_API}${image.imageUrl}`
                    ? "border-indigo-400 ring-2 ring-indigo-400/50"
                    : "border-transparent opacity-70 hover:opacity-100 hover:border-white/30"
                }`}
              >
                <img
                  src={`${process.env.REACT_APP_API}${image.imageUrl}`}
                  alt={`X-ray ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
            {data?.map((item: any, i: number) => (
              <button
                key={`local-${i}`}
                type="button"
                onClick={() => selectImage(item.url)}
                className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  selectedImage === item.url
                    ? "border-indigo-400 ring-2 ring-indigo-400/50"
                    : "border-transparent opacity-70 hover:opacity-100 hover:border-white/30"
                }`}
              >
                <img
                  src={item.url}
                  alt={`Image ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            disabled={imageList.length <= 1}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next image"
          >
            <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImagePreview;
